import { replaceUnderScoreAddSpace } from "@/utils/utils";
import { HOME_CARE_ROS_OPTION_LABELS } from "./home-care-ros-constant";

export interface HomeCareRosOption {
  label: string;
  hasFreeText: boolean;
}

export type HomeCareRosSelection = Record<string, string>;

const splitOptions = (value?: string): string[] =>
  (value ?? "")
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);

const splitToken = (token: string): [string, string | undefined] => {
  const index = token.indexOf(":");
  return index < 0
    ? [token, undefined]
    : [token.slice(0, index), token.slice(index + 1)];
};

export const homeCareRosOptionLabel = (label: string): string =>
  HOME_CARE_ROS_OPTION_LABELS[label] ?? replaceUnderScoreAddSpace(label);

/** Parses "WNL,Allergy:freeText" into options; ":freeText" marks an option with a text box. */
export const parseHomeCareRosOptions = (
  catalog?: string
): HomeCareRosOption[] =>
  splitOptions(catalog).map((token) => {
    const [label, marker] = splitToken(token);
    return { label, hasFreeText: marker === "freeText" };
  });

/** Maps each ticked option label to its free text ("" when it has none). */
export const parseHomeCareRosSelection = (
  value?: string
): HomeCareRosSelection =>
  splitOptions(value).reduce<HomeCareRosSelection>((selection, token) => {
    const [label, text] = splitToken(token);
    selection[label] = text ? decodeURIComponent(text) : "";
    return selection;
  }, {});

/** URI-encodes typed text, "_" included: a raw "_" or "|" in the stored value is read as option-key syntax. */
export const encodeHomeCareRosText = (text: string): string =>
  encodeURIComponent(text).replace(/_/g, "%5F");

/** Free text is URI-encoded so commas and colons cannot break the string. */
export const serializeHomeCareRosSelection = (
  options: HomeCareRosOption[],
  selection: HomeCareRosSelection
): string =>
  options
    .filter((option) => option.label in selection)
    .map((option) => {
      const text = selection[option.label];
      return option.hasFreeText && text
        ? `${option.label}:${encodeHomeCareRosText(text)}`
        : option.label;
    })
    .join(",");

/** Safety answers are "key:value" tokens; multi-select values are joined with "|". */
export const parseHomeCareRosAnswers = (
  value?: string
): Record<string, string> =>
  splitOptions(value).reduce<Record<string, string>>((answers, token) => {
    const [key, answer] = splitToken(token);
    answers[key] = answer ?? "";
    return answers;
  }, {});

export const serializeHomeCareRosAnswers = (
  answers: Record<string, string>
): string =>
  Object.entries(answers)
    .filter(([, answer]) => answer)
    .map(([key, answer]) => `${key}:${answer}`)
    .join(",");
